import { useEffect, useMemo, useState } from "react";
import {
  History,
  Pencil,
  Search,
  Trash2,
  FileText,
  UsersRound,
  DollarSign,
  CalendarDays,
  X,
} from "lucide-react";

import QuoteEditModal from "@/components/QuoteEditModal";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { formatBoliviaDate } from "@/lib/dateTime";

export default function QuoteHistoryPage() {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();

  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [editingQuote, setEditingQuote] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);

    Promise.all([
      api.get("/quotes"),
      api.get("/clients"),
    ])
      .then(([quotesResponse, clientsResponse]) => {
        setQuotes(quotesResponse.data);
        setClients(clientsResponse.data);
      })
      .catch((error) => {
        setMessage(apiError(error));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filteredQuotes = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return quotes;

    return quotes.filter((quote) => {
      const text = [
        quote.client?.nombre,
        quote.client?.ci,
        quote.lot?.manzano,
        quote.lot?.lote,
        quote.vendedor,
      ]
        .join(" ")
        .toLowerCase();

      return text.includes(term);
    });
  }, [query, quotes]);

  const uniqueClients = useMemo(() => {
    return new Set(
      quotes
        .map((quote) => quote.client?.ci || quote.client?.nombre)
        .filter(Boolean)
    ).size;
  }, [quotes]);

  const totalInitial = useMemo(() => {
    return quotes.reduce(
      (total, quote) =>
        total + Number(quote.valor_cuota || quote.inicial || 0),
      0
    );
  }, [quotes]);

  const latestDate = useMemo(() => {
    if (!quotes.length) return "—";

    const dates = quotes
      .map((quote) => quote.updated_at || quote.created_at)
      .filter(Boolean)
      .sort();

    if (!dates.length) return "—";

    return formatBoliviaDate(dates[dates.length - 1]);
  }, [quotes]);

  const saveQuote = (updatedQuote) => {
    setQuotes((current) =>
      current.map((quote) =>
        quote.id === updatedQuote.id ? updatedQuote : quote
      )
    );

    setEditingQuote(null);
    setMessage("Cotización actualizada y recalculada correctamente.");
  };

  const deleteQuote = async (quote) => {
    if (user?.role !== "administrador") {
      return;
    }

    const confirmed = window.confirm(
      `¿Está seguro de eliminar esta cotización?\n\n` +
        `Cliente: ${quote.client?.nombre || "Sin nombre"}\n` +
        `Lote: ${quote.lot?.manzano || "—"} · ${quote.lot?.lote || "—"}\n\n` +
        `Esta acción no se puede deshacer.`
    );

    if (!confirmed) {
      return;
    }

    setMessage("");

    try {
      await api.delete(`/quotes/${quote.id}`);

      setQuotes((current) =>
        current.filter((item) => item.id !== quote.id)
      );

      setMessage("Cotización eliminada correctamente.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <div className="quote-history-page">

      {/* HEADER */}
      <section className="quote-history-header">
        <div className="quote-history-heading">
          <div className="quote-history-eyebrow">
            <History size={15} />
            SEGUIMIENTO COMERCIAL
          </div>

          <h1>Cotizaciones históricas</h1>

          <p>
            Consulta, actualiza y conserva la trazabilidad de cada propuesta
            comercial.
          </p>
        </div>

        <div className="quote-history-header-icon">
          <History size={28} />
        </div>
      </section>

      {/* RESUMEN */}
      <section className="quote-history-summary">

        <div className="quote-summary-card">
          <div className="quote-summary-icon quote-summary-blue">
            <FileText size={22} />
          </div>

          <div>
            <span>Cotizaciones</span>
            <strong>{quotes.length}</strong>
            <small>Propuestas registradas</small>
          </div>
        </div>

        <div className="quote-summary-card">
          <div className="quote-summary-icon quote-summary-purple">
            <UsersRound size={22} />
          </div>

          <div>
            <span>Clientes</span>
            <strong>{uniqueClients}</strong>
            <small>Clientes con propuestas</small>
          </div>
        </div>

        <div className="quote-summary-card">
          <div className="quote-summary-icon quote-summary-green">
            <DollarSign size={22} />
          </div>

          <div>
            <span>Cuotas registradas</span>
            <strong>{formatAmount(totalInitial)}</strong>
            <small>Valor acumulado</small>
          </div>
        </div>

        <div className="quote-summary-card">
          <div className="quote-summary-icon quote-summary-orange">
            <CalendarDays size={22} />
          </div>

          <div>
            <span>Última actualización</span>
            <strong className="quote-summary-date">
              {latestDate}
            </strong>
            <small>Actividad comercial</small>
          </div>
        </div>

      </section>

      {/* PANEL PRINCIPAL */}
      <section className="quote-history-panel">

        <div className="quote-history-toolbar">

          <div>
            <div className="quote-history-section-label">
              REGISTRO COMERCIAL
            </div>

            <h2>
              Propuestas
              <span>{filteredQuotes.length}</span>
            </h2>

            <p>
              Historial de cotizaciones generadas en el sistema.
            </p>
          </div>

          <div className="quote-history-search">

            <Search size={18} />

            <input
              data-testid="quote-history-search-input"
              id="quote-history-search"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar cliente, CI, manzano o lote..."
              value={query}
            />

            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Limpiar búsqueda"
              >
                <X size={17} />
              </button>
            )}

          </div>

        </div>

        {message && (
          <div className="quote-history-message">
            {message}
          </div>
        )}

        {loading ? (
          <div className="quote-history-loading">
            <div className="quote-history-spinner" />
            <span>Cargando cotizaciones...</span>
          </div>
        ) : (
          <div className="quote-history-table-wrapper">

            <table className="quote-history-table">

              <thead>
                <tr>
                  <th>CLIENTE</th>
                  <th>LOTE</th>
                  <th>CONDICIÓN</th>
                  <th>CUOTA</th>
                  <th>CREADA POR</th>
                  <th>ACTUALIZACIÓN</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>

              <tbody>

                {filteredQuotes.map((quote) => (

                  <tr
                    data-testid={`quote-history-row-${quote.id}`}
                    key={quote.id}
                  >

                    {/* CLIENTE */}
                    <td>
                      <div className="quote-client-cell">

                        <div className="quote-client-avatar">
                          {(quote.client?.nombre || "C")
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                        </div>

                        <div>
                          <strong>
                            {quote.client?.nombre || "Sin nombre"}
                          </strong>

                          <small>
                            CI {quote.client?.ci || "—"}
                          </small>
                        </div>

                      </div>
                    </td>

                    {/* LOTE */}
                    <td>
                      <div className="quote-lot-cell">

                        <strong>
                          Mz. {quote.lot?.manzano || "—"}
                        </strong>

                        <span>
                          Lote {quote.lot?.lote || "—"}
                        </span>

                      </div>
                    </td>

                    {/* CONDICIÓN */}
                    <td>
                      <div className="quote-condition">

                        <span className="quote-condition-badge">
                          {quote.inicial_porcentaje}% inicial
                        </span>

                        <small>
                          {quote.meses
                            ? `${quote.meses} meses`
                            : "Contado"}
                        </small>

                      </div>
                    </td>

                    {/* CUOTA */}
                    <td>
                      <div className="quote-amount">
                        {formatAmount(
                          quote.valor_cuota || quote.inicial
                        )}
                      </div>
                    </td>

                    {/* VENDEDOR */}
                    <td>
                      <div className="quote-owner">
                        <span className="quote-owner-dot" />
                        <span>
                          {quote.vendedor || "Administración"}
                        </span>
                      </div>
                    </td>

                    {/* FECHA */}
                    <td>
                      <div className="quote-update">

                        <strong>
                          {formatBoliviaDate(
                            quote.updated_at || quote.created_at
                          )}
                        </strong>

                        <small>
                          {quote.edit_history?.length || 0} revisiones
                        </small>

                      </div>
                    </td>

                    {/* ACCIONES */}
                    <td>
                      <div className="quote-actions">

                        <button
                          aria-label={`Editar cotización de ${quote.client?.nombre}`}
                          className="quote-action-button quote-edit-button"
                          data-testid={`edit-quote-${quote.id}`}
                          onClick={() => setEditingQuote(quote)}
                          title="Editar cotización"
                          type="button"
                        >
                          <Pencil size={16} />
                        </button>

                        {user?.role === "administrador" && (
                          <button
                            aria-label={`Eliminar cotización de ${quote.client?.nombre}`}
                            className="quote-action-button quote-delete-button"
                            data-testid={`delete-quote-${quote.id}`}
                            onClick={() => deleteQuote(quote)}
                            title="Eliminar cotización"
                            type="button"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}

                      </div>
                    </td>

                  </tr>

                ))}

                {!filteredQuotes.length && (
                  <tr>
                    <td colSpan="7">
                      <div className="quote-history-empty">
                        <FileText size={40} />
                        <strong>
                          No hay cotizaciones para mostrar
                        </strong>
                        <span>
                          {query
                            ? "No encontramos resultados para esa búsqueda."
                            : "Todavía no existen propuestas registradas."}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}

              </tbody>

            </table>

          </div>
        )}

      </section>

      {editingQuote && (
        <QuoteEditModal
          clients={clients}
          onClose={() => setEditingQuote(null)}
          onSaved={saveQuote}
          quote={editingQuote}
        />
      )}

    </div>
  );
}